import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useReveal } from "../hooks/useReveal";

export default function ModelsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("category") || "All";

  const [models, setModels] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(initialCat);
  const rootRef = useReveal([models.length, active]);

  useEffect(() => {
    api.get("/models").then(({ data }) => setModels(data));
    api.get("/categories").then(({ data }) => setCats(data.categories || []));
  }, []);

  const filtered = useMemo(
    () => (active === "All" ? models : models.filter((m) => m.category === active)),
    [models, active]
  );

  const handleCat = (c) => {
    setActive(c);
    if (c === "All") searchParams.delete("category");
    else searchParams.set("category", c);
    setSearchParams(searchParams);
  };

  return (
    <div ref={rootRef} className="bg-white text-[#1d1d1f]" data-testid="models-page">
      <Navbar />

      <section className="pt-36 md:pt-44 pb-12 md:pt-44 md:pb-16 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 reveal">
          <div className="overline">The Full Catalog</div>
          <h1 className="h1-hero text-5xl md:text-7xl mt-4">
            Every Bobcat.
            <br />
            <span className="text-[#FF6A13]">Ready to work.</span>
          </h1>
          <p className="mt-5 text-lg text-[#86868B] max-w-2xl">
            Explore the Bobcat lineup stocked and supported by Bhumi Equipments.
          </p>
        </div>
      </section>

      <section className="sticky top-[72px] md:top-[104px] z-30 bg-white/95 backdrop-blur border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-2 overflow-x-auto">
          {["All", ...cats].map((c) => (
            <button
              key={c}
              onClick={() => handleCat(c)}
              data-testid={`cat-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                active === c
                  ? "bg-[#1d1d1f] text-white"
                  : "bg-[#f5f5f7] text-[#1d1d1f] hover:bg-black/10"
              }`}
            >
              {c}
            </button>
          ))}
          <Link
            to="/compare"
            className="ml-auto whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold bg-[#ff6a13] text-white hover:bg-[#e85a0d] inline-flex items-center gap-1.5"
            data-testid="goto-compare"
          >
            Compare Models <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((m) => (
              <Link
                key={m.id}
                to={`/models/${m.id}`}
                className="reveal group rounded-3xl overflow-hidden bg-[#f5f5f7] hover:bg-white border border-transparent hover:border-[#FF6A13]/30 hover:shadow-xl transition-all duration-500"
                data-testid={`model-card-${m.id}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-widest text-[#FF6A13] font-bold">
                    {m.category}
                  </div>
                  <div className="mt-2 font-display text-2xl font-bold">
                    {m.name}
                  </div>
                  <div className="mt-2 text-sm text-[#86868B]">
                    {m.short_desc}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1d1d1f] group-hover:text-[#FF6A13]">
                    View specs <ArrowUpRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-20 text-center text-[#86868B]">
              No models in this category yet.
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
